import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';

const PlanContext = createContext(null);

export const usePlan = () => useContext(PlanContext);

export const PlanProvider = ({ children }) => {
    const [planData, setPlanData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchPlanContext = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) { setLoading(false); return; }

            const response = await axios.get(`${config.API_BASE_URL}/api/subscription/current`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setPlanData(response.data.data);
            }
        } catch (err) {
            // No subscription data available — free tier
            console.log('[PlanContext] No subscription data:', err.response?.status);
            // A failed refresh (server or database hiccup) must not wipe what we
            // already know: with no plan data every module reads as allowed, so a
            // Starter account would see Marketing open until the next refresh.
            setPlanData((prev) => (err.response?.status === 404 ? null : prev));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlanContext();
        // Refresh every 5 minutes
        const interval = setInterval(fetchPlanContext, 5 * 60 * 1000);
        // Also when the tab is opened again, so a plan changed in the admin panel
        // shows up without waiting for the timer.
        const onVisible = () => { if (document.visibilityState === 'visible') fetchPlanContext(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', fetchPlanContext);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', fetchPlanContext);
        };
    }, [fetchPlanContext]);

    // Platform-wide enforcement switch (platform_settings.subscription_enforcement_mode).
    // The API only returns 403 when this is 'enforce'; anything else is log-only.
    // The panel must follow the same switch, otherwise the UI locks a page whose
    // API calls all succeed — you could deploy an agent and then be told to
    // upgrade when you reopened it.
    const isEnforcing = planData?.enforcement_mode === 'enforce';

    // Check if module is enabled. Accepts a comma-separated list — access is
    // granted if ANY of the slugs is in the plan (e.g. the AI Agent pages serve
    // both chat and voice agents).
    const hasModule = (slug) => {
        if (!isEnforcing) return true;
        if (!planData?.modules) return true; // No plan data = allow (fail-open)
        const slugs = String(slug).split(',').map((s) => s.trim()).filter(Boolean);
        return slugs.some((s) => {
            const mod = planData.modules[s];
            if (!mod) return true; // Unknown module = allow
            return mod.enabled || mod.is_free_default;
        });
    };

    // Check if at limit
    const isAtLimit = (slug) => {
        if (!isEnforcing) return false;
        if (!planData?.modules || !planData?.usage) return false;
        const slugs = String(slug).split(',').map((s) => s.trim()).filter(Boolean);
        return slugs.every((s) => {
            const mod = planData.modules[s];
            if (!mod || !mod.is_countable || mod.limit === null) return false;
            const used = planData.usage[s]?.used || 0;
            return used >= mod.limit;
        });
    };

    // Get usage for module
    const getUsage = (slug) => {
        if (!planData?.usage?.[slug]) return null;
        return planData.usage[slug];
    };

    // Get limit for module
    const getLimit = (slug) => {
        if (!planData?.modules?.[slug]) return null;
        return planData.modules[slug].limit;
    };

    // Get subscription info
    const getSubscription = () => planData?.subscription || null;

    // Force refresh
    const refresh = () => fetchPlanContext();

    return (
        <PlanContext.Provider value={{
            planData,
            loading,
            isEnforcing,
            hasModule,
            isAtLimit,
            getUsage,
            getLimit,
            getSubscription,
            refresh,
        }}>
            {children}
        </PlanContext.Provider>
    );
};

export default PlanContext;
